import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';
import PageHeader from '../../../components/PageHeader';
import { 
  getAvailableYears, 
  getYearStats, 
  getVideosForYear,
  getVideoById
} from './data';
import './VideoGallery.css';
import usePageSearchIndex from '../../../hooks/usePageSearchIndex';

const VideoGallery = () => {
  const { isMarathi } = useLanguage();
  const { year, videoId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  // Determine current view
  const showingYearList = !year;
  const selectedYear = year ? parseInt(year) : null;
  const selectedVideoId = videoId ? parseInt(videoId) : null;

  // Get data based on current view
  const availableYears = getAvailableYears();
  const yearVideos = selectedYear ? getVideosForYear(selectedYear) : [];
  const currentVideo = (selectedYear && selectedVideoId) 
    ? getVideoById(selectedYear, selectedVideoId) 
    : null;

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  // Auto-index Video Gallery page content for search
  usePageSearchIndex({
    mr: ['व्हिडिओ', 'गॅलरी', 'व्हिडिओज', 'वर्ष'],
    en: ['video', 'gallery', 'videos', 'year']
  });

  // Auto-redirect to latest year when no year is selected
  useEffect(() => {
    if (showingYearList && availableYears.length > 0) {
      navigate(`/video-gallery/${availableYears[0]}`, { replace: true });
    }
  }, [showingYearList, availableYears, navigate]);

  const handleYearClick = (year) => {
    navigate(`/video-gallery/${year}`);
  };

  const handleVideoClick = (videoId) => {
    navigate(`/video-gallery/${selectedYear}/${videoId}`);
  };

  const handleBackToYear = () => {
    navigate(`/video-gallery/${selectedYear}`);
  };

  const renderVideoPlayer = (video) => {
    if (!video) return null;

    if (video.type === 'youtube' || video.videoUrl?.includes('youtube.com') || video.videoUrl?.includes('youtu.be')) {
      // Extract YouTube video ID
      let extractedVideoId = '';
      if (video.videoUrl.includes('youtube.com/embed/')) {
        extractedVideoId = video.videoUrl.split('youtube.com/embed/')[1].split('?')[0];
      } else if (video.videoUrl.includes('youtu.be/')) {
        extractedVideoId = video.videoUrl.split('youtu.be/')[1].split('?')[0];
      } else if (video.videoUrl.includes('youtube.com/watch?v=')) {
        extractedVideoId = video.videoUrl.split('v=')[1].split('&')[0];
      } else {
        // Assume it's already just the video ID
        extractedVideoId = video.videoUrl;
      }
      
      return (
        <div className="video-player-container">
          <iframe
            src={`https://www.youtube.com/embed/${extractedVideoId}`}
            title={isMarathi() ? video.title?.marathi : video.title?.english}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="video-player"
          />
        </div>
      );
    } else if (video.type === 'vimeo' || video.videoUrl?.includes('vimeo.com')) {
      // Extract Vimeo video ID
      const extractedVideoId = video.videoUrl.split('vimeo.com/')[1]?.split('?')[0];
      
      return (
        <div className="video-player-container">
          <iframe
            src={`https://player.vimeo.com/video/${extractedVideoId}`}
            title={isMarathi() ? video.title?.marathi : video.title?.english}
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="video-player"
          />
        </div>
      );
    } else {
      // Direct video file
      return (
        <div className="video-player-container">
          <video
            controls
            poster={video.poster || video.thumbnail}
            className="video-player"
          >
            <source src={video.videoUrl} type="video/mp4" />
            {isMarathi() ? 'तुमचा ब्राउझर व्हिडिओ समर्थन करत नाही' : 'Your browser does not support the video tag.'}
          </video>
        </div>
      );
    }
  };

  const pageHeaderData = {
    title: {
      marathi: 'व्हिडिओ गॅलरी',
      english: 'Video Gallery'
    },
    subtitle: {
      marathi: 'मंडळाच्या विविध कार्यक्रमांची व्हिडिओ',
      english: 'Videos from various board programs'
    }
  };

  if (isLoading) {
    return (
      <div className="video-gallery">
        <div className="video-gallery-container">
          <PageHeader 
            title={pageHeaderData.title}
            subtitle={pageHeaderData.subtitle}
          />
          <div className="loading-message">
            <p>{isMarathi() ? 'लोड होत आहे...' : 'Loading...'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="video-gallery">
      <div className="video-gallery-container">
        <PageHeader 
          title={pageHeaderData.title}
          subtitle={pageHeaderData.subtitle}
        />

        <div className="video-gallery-content">
          {currentVideo ? (
            /* Single Video View */
            <div className="video-detail-container">
              <div className="video-detail-header">
                <button 
                  onClick={handleBackToYear}
                  className="back-to-videos-btn"
                >
                  ← {isMarathi() ? 'व्हिडिओकडे परत जा' : 'Back to Videos'}
                </button>
              </div>

              <div className="video-detail-content">
                {renderVideoPlayer(currentVideo)}
                <div className="video-detail-info">
                  <h2 className="video-detail-title">
                    {isMarathi() ? currentVideo.title?.marathi : currentVideo.title?.english}
                  </h2>
                  {currentVideo.description && (
                    <p className="video-detail-description">
                      {isMarathi() ? currentVideo.description?.marathi : currentVideo.description?.english}
                    </p>
                  )}
                  {currentVideo.date && (
                    <p className="video-detail-date">
                      {isMarathi() ? 'तारीख' : 'Date'}: {new Date(currentVideo.date).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : selectedYear ? (
            /* Videos Grid View */
            <div className="videos-container">
              <div className="videos-header">
                <p className="videos-count">
                  {isMarathi() 
                    ? `${yearVideos.length} व्हिडिओ उपलब्ध` 
                    : `${yearVideos.length} Videos Available`}
                </p>
              </div>

              <div className="videos-grid">
                {yearVideos.length > 0 ? (
                  yearVideos.map((video) => (
                    <div
                      key={video.id}
                      className="video-item"
                      onClick={() => handleVideoClick(video.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleVideoClick(video.id);
                        }
                      }}
                      aria-label={`${isMarathi() ? 'व्हिडिओ उघडा' : 'Open video'}: ${isMarathi() ? video.title?.marathi || '' : video.title?.english || ''}`}
                    >
                      <div className="video-thumbnail">
                        <img
                          src={video.thumbnail || video.poster}
                          alt={isMarathi() ? (video.title?.marathi || '') : (video.title?.english || '')}
                          className="video-thumbnail-image"
                          loading="lazy"
                        />
                        <div className="video-play-overlay">
                          <svg className="play-icon" width="64" height="64" viewBox="0 0 24 24" fill="white">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        </div>
                      </div>
                      <div className="video-info">
                        <h4 className="video-title">
                          {isMarathi() ? (video.title?.marathi || '') : (video.title?.english || '')}
                        </h4>
                        {video.date && (
                          <p className="video-date">
                            {new Date(video.date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-videos-message">
                    <p>
                      {isMarathi() 
                        ? 'या वर्षासाठी कोणतेही व्हिडिओ उपलब्ध नाहीत' 
                        : 'No videos available for this year'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Year List View */
            <div className="years-container">
              <div className="years-grid">
                {availableYears.map((year) => {
                  const yearStats = getYearStats(year);
                  return (
                    <div
                      key={year}
                      className="year-card"
                      onClick={() => handleYearClick(year)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleYearClick(year);
                        }
                      }}
                    >
                      <div className="year-card-image">
                        {yearStats.thumbnail && (
                          <img
                            src={yearStats.thumbnail}
                            alt={`${year} ${isMarathi() ? 'व्हिडिओ' : 'Videos'}`}
                            className="year-card-thumbnail"
                            loading="lazy"
                          />
                        )}
                        <div className="year-card-overlay">
                          <div className="year-card-content">
                            <h3 className="year-card-title">{year}</h3>
                            <p className="year-card-count">
                              {yearStats.videoCount} {isMarathi() ? 'व्हिडिओ' : 'Videos'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoGallery;

